import * as React from "react";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from "@mui/icons-material/Warning";
import { Checkbox, FormControlLabel, Grid, Typography, Link } from "@mui/material";
import { ACCIDENT_FORM_URL } from "@/pages/CarUnderuseEvents/CarNoopEventsConstants";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
}

const BootstrapDialogTitle = (props: DialogTitleProps) => {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle
      sx={{
        m: 0,
        p: 2,
        backgroundColor: "#E71A19",
        color: (theme) => theme.palette.grey[300],
        textAlign: "center",
      }}
      {...other}
    >
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[300],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
};

export interface CarNoopConfirmDialogProps {
  visible: boolean;
  headerTitle: string;
  onOk: () => void;
  onCancel: () => void;
  message: string;
  showWarning: boolean;
}

export const CarNoopConfirmDialog = (props: CarNoopConfirmDialogProps) => {
  const {
    visible,
    onCancel,
    onOk,
    headerTitle,
    message,
    showWarning,
  } = props;

  const [isChecked, setIsChecked] = React.useState(false);

  const handleClose = React.useCallback(() => {
    setIsChecked(false);
    onCancel();
  }, [onCancel]);

  const handleOk = React.useCallback(() => {
    setIsChecked(false);
    onOk();
  }, [onOk]);

  const handleCheckboxChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setIsChecked(event.target.checked);
    },
    []
  );

  return (
    <div>
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={visible}
      >
        {headerTitle && (
          <BootstrapDialogTitle
            id="customized-dialog-title"
            onClose={handleClose}
          >
            {headerTitle}
          </BootstrapDialogTitle>
        )}
        <DialogContent dividers sx={{ textAlign: "left" }}>
          <Typography sx={{ mb: 1, whiteSpace: "pre-line" }}>
            {message}
          </Typography>
          {showWarning ? (
            <>
              <Grid container alignItems="center" sx={{ backgroundColor: "#ffe0e0", borderRadius: 1, p: 1, my: 2 }}>
                <Grid item xs="auto">
                  <WarningIcon
                    aria-label="warning"
                    sx={{
                      color: "#E71A19",
                      fontSize: 40,
                      mx: 2,
                    }}
                  />
                </Grid>
                <Grid item xs>
                  <Typography sx={{ whiteSpace: "pre-line", color: "#a00000" }}>
                    上記の内容で登録します。{"\n"}
                    事実確認の上、未報告の事故がございましたら、急ぎ
                    <Link
                      href={ACCIDENT_FORM_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: "#1976d2", textDecoration: "underline" }}
                    >
                      こちら
                    </Link>
                    よりご報告ください。
                  </Typography>
                </Grid>
              </Grid>
              <Typography sx={{ whiteSpace: "pre-line" }}>
                車両の未稼働理由として事故以外の理由を選択されていたものの、実際は事故が発生しており、「事故報告遅延」に該当した事例もございます。未報告の事故がないか改めてご確認ください。
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isChecked}
                    onChange={handleCheckboxChange}
                  />
                }
                label="未報告の事故はないことを確認しました"
                sx={{
                  my: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  '& .MuiFormControlLabel-label': {
                    fontWeight: 'bold'
                  }
                }}
              />
            </>
          ) : (
            <Typography sx={{ whiteSpace: "pre-line", mt: 2 }}>
              上記の内容で登録します
            </Typography>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            alignSelf: "center",
          }}
        >
          <Button
            onClick={handleClose}
            sx={{
              backgroundColor: "#555",
              color: "#FFF",
              ":hover": { color: "#000" },
            }}
          >
            キャンセル
          </Button>
          <Button
            className="custom-delete-btn"
            onClick={handleOk}
            disabled={!isChecked && showWarning}
            sx={{
              color: "#FFF",
              ":hover": { color: "#000" },
              backgroundColor: (theme) => theme.palette.primary.main,
              "&.Mui-disabled": {
                backgroundColor: "#ccc",
                color: "#888",
              },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </BootstrapDialog>
    </div>
  );
};
